import * as React from 'react';
import { styled } from '@linaria/react';
import { Eraser, Pencil } from 'lucide-react';

import Demo from '@/components/Demo';
import Space from '@/components/Space';
import ButtonPrimary from '@/components/ButtonPlayful/ButtonPrimary';

import {
  Svg,
  CodeSnippet,
  Row,
} from './BasicShapesDemo/BasicShapes.shared';

const WIDTH = 280;
const HEIGHT = 320;
const DRAW_DURATION = 3000;

const POINTS = createPoints(WIDTH, HEIGHT);
const POINTS_STRING = POINTS.map((p) => `${p.x},${p.y}`).join('\n  ');

function SelfDrawingDemo() {
  const [isDrawn, setIsDrawn] = React.useState(false);
  const [pathLength, setPathLength] = React.useState(763);
  const ref = React.useRef<any>(null);

  React.useEffect(() => {
    if (ref.current) {
      setPathLength(Math.ceil(ref.current.getTotalLength()));
    }
  }, []);

  return (
    <Demo outputWrapper={OutputWrapper} initialValues={{}}>
      {({}) => {
        const code = `\
<style>
  polygon {
    stroke-dasharray: ${pathLength}, 10000;
    stroke-dashoffset: ${isDrawn ? 0 : pathLength};
    transition:
      stroke-dashoffset ${DRAW_DURATION}ms;
  }
</style>
\n \n
<svg viewBox="0 0 ${WIDTH} ${HEIGHT}">
  <polygon points="..." />
</svg>\
`;
        return (
          <>
            <StyledRow>
              <SvgWrapper>
                <Svg
                  width={WIDTH}
                  height={HEIGHT}
                  viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                >
                  <TraceShape points={POINTS_STRING} />
                  <Shape
                    ref={ref}
                    points={POINTS_STRING}
                    style={{
                      '--path-length': pathLength,
                    }}
                    strokeDashoffset={isDrawn ? 0 : pathLength}
                  />
                </Svg>
              </SvgWrapper>
              <StyledCodeSnippet code={code} />
            </StyledRow>
            <StyledButton
              textAlign="true-center"
              Icon={isDrawn ? Eraser : Pencil}
              onClick={() => setIsDrawn(!isDrawn)}
            >
              {isDrawn ? 'Erase' : 'Draw shape'}
            </StyledButton>
          </>
        );
      }}
    </Demo>
  );
}

function createPoints(width: number, height: number) {
  const centerX = width / 2;
  const centerY = height / 2;

  const size = Math.min(width, height) * 0.75;
  const outerRadius = size / 2;
  const innerRadius = outerRadius * 0.382;

  const points: Array<{ x: number; y: number }> = [];

  for (let i = 0; i < 10; i++) {
    const angle = (i * Math.PI) / 5 - Math.PI / 2;
    const radius = i % 2 === 0 ? outerRadius : innerRadius;

    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);

    points.push({ x, y });
  }

  return points;
}

const BREAKPOINT = 37;

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: var(--color-background);
`;

const StyledRow = styled(Row)`
  @container (max-width: ${BREAKPOINT}rem) {
    flex-direction: column-reverse;
    align-items: center;
  }
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  overflow: hidden;

  @container (max-width: ${BREAKPOINT}rem) {
    width: 100%;
  }
`;

const SvgWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const BaseShape = styled.polygon`
  width: ${WIDTH / 2}px;
  height: ${WIDTH / 2}px;
  fill: none;
  stroke-width: 5px;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: var(--path-length), var(--path-length);
`;

const TraceShape = styled(BaseShape)`
  stroke: var(--color-text);
  stroke-opacity: 0.125;
`;
const Shape = styled(BaseShape)`
  /* prettier-ignore */
  --spring: cubic-bezier(0.535, 0.199, 0.228, 0.928);

  stroke: var(--wham-complementary-500);
  transition: stroke-dashoffset ${DRAW_DURATION}ms var(--spring);
`;

const StyledButton = styled(ButtonPrimary)`
  margin-inline: auto;
  width: 17.5rem;
  max-width: 100%;
`;

export default SelfDrawingDemo;
